/**
 * Shared markdown renderer for all AI-generated text in Vojomo.
 *
 * Handles: # h1, ## h2, ### h3, - bullets, 1. numbered lists,
 * | tables |, --- horizontal rules, **bold**, *italic*, blank lines.
 *
 * Two rendering contexts (per spec Section 14.2):
 *  - "panel" (default): compact, scannable — for in-panel Team View sections
 *  - "studio": document-like, richer — for Studio modals, references PDF formatting
 *
 * All elements use fontFamily:"inherit" so they never pick up browser
 * serif defaults from h1–h6 elements.
 */
import React from "react";

export type RenderContext = "panel" | "studio";

function renderInline(text: string): React.ReactNode[] {
  // Split on **bold** and *italic* spans (non-greedy, no nested asterisks)
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4)
      return <strong key={i} style={{ fontWeight: 600 }}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2)
      return <em key={i}>{part.slice(1, -1)}</em>;
    return part;
  });
}

const TABLE_SEP = /^\|[\s|:\-]+\|$/;

/** Vojomo brand purple used for document headings in Studio context */
const VOJOMO_PURPLE = "#2c1561";

export function renderMarkdown(
  markdown: string,
  opts: { baseFontSize?: number; context?: RenderContext } = {}
): React.ReactNode[] {
  const ctx = opts.context ?? "panel";
  const base = opts.baseFontSize ?? (ctx === "studio" ? 14 : 12);
  const lines = markdown.split("\n");
  const nodes: React.ReactNode[] = [];

  // Style presets per context
  const h1Style: React.CSSProperties = ctx === "studio"
    ? { fontSize: base + 6, fontWeight: 700, color: VOJOMO_PURPLE, margin: "24px 0 10px", lineHeight: 1.3, fontFamily: "inherit", borderBottom: "2px solid #e5e7eb", paddingBottom: 6 }
    : { fontSize: base + 2, fontWeight: 700, color: "#0f172a", margin: "14px 0 5px", lineHeight: 1.35, fontFamily: "inherit" };

  const h2Style: React.CSSProperties = ctx === "studio"
    ? { fontSize: base + 3, fontWeight: 700, color: VOJOMO_PURPLE, margin: "20px 0 8px", lineHeight: 1.35, fontFamily: "inherit" }
    : { fontSize: base + 1, fontWeight: 700, color: "#0f172a", margin: "12px 0 4px", lineHeight: 1.35, fontFamily: "inherit" };

  const h3Style: React.CSSProperties = ctx === "studio"
    ? { fontSize: base + 1, fontWeight: 600, color: "#475569", margin: "16px 0 6px", textTransform: "uppercase" as const, letterSpacing: "0.04em", fontFamily: "inherit" }
    : { fontSize: base, fontWeight: 600, color: "#475569", margin: "10px 0 3px", textTransform: "uppercase" as const, letterSpacing: "0.04em", fontFamily: "inherit" };

  const pStyle: React.CSSProperties = ctx === "studio"
    ? { fontSize: base, color: "#374151", margin: "4px 0", lineHeight: 1.7, fontFamily: "inherit" }
    : { fontSize: base, color: "#334155", margin: "2px 0", lineHeight: 1.6, fontFamily: "inherit" };

  const bulletTextStyle: React.CSSProperties = ctx === "studio"
    ? { fontSize: base, color: "#374151", lineHeight: 1.7, fontFamily: "inherit" }
    : { fontSize: base, color: "#334155", lineHeight: 1.6, fontFamily: "inherit" };

  const blankHeight = ctx === "studio" ? 10 : 6;
  const hrMargin = ctx === "studio" ? "16px 0" : "10px 0";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // H1 (# but not ##)
    if (/^# [^#]/.test(line)) {
      nodes.push(
        <div key={i} style={h1Style}>
          {renderInline(line.slice(2))}
        </div>
      );
      continue;
    }

    // H2 (## but not ###)
    if (/^## [^#]/.test(line)) {
      nodes.push(
        <div key={i} style={h2Style}>
          {renderInline(line.slice(3))}
        </div>
      );
      continue;
    }

    // H3
    if (/^### /.test(line)) {
      nodes.push(
        <div key={i} style={h3Style}>
          {renderInline(line.slice(4))}
        </div>
      );
      continue;
    }

    // Table separator row (|---|---|) — skip, but mark next non-sep table row as header
    if (TABLE_SEP.test(trimmed)) {
      continue;
    }

    // Table row
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      const nextLine = lines[i + 1]?.trim() ?? "";
      const isHeader = TABLE_SEP.test(nextLine);
      const cells = trimmed
        .split("|")
        .slice(1, -1)
        .map((c) => c.trim());
      nodes.push(
        <div key={i} style={{
          display: "flex",
          gap: ctx === "studio" ? 12 : 8,
          padding: ctx === "studio" ? "6px 0" : "4px 0",
          borderBottom: `1px solid ${ctx === "studio" ? "#e5e7eb" : "#f1f5f9"}`,
        }}>
          {cells.map((cell, ci) => (
            <div
              key={ci}
              style={{
                flex: 1,
                fontSize: base,
                fontWeight: isHeader ? 600 : 400,
                color: isHeader ? (ctx === "studio" ? VOJOMO_PURPLE : "#0f172a") : "#334155",
                fontFamily: "inherit",
              }}
            >
              {renderInline(cell)}
            </div>
          ))}
        </div>
      );
      continue;
    }

    // Bullet
    if (line.startsWith("- ") || line.startsWith("* ")) {
      nodes.push(
        <div key={i} style={{ display: "flex", gap: 6, margin: ctx === "studio" ? "3px 0" : "2px 0", paddingLeft: 8 }}>
          <span style={{ color: ctx === "studio" ? VOJOMO_PURPLE : "#94a3b8", flexShrink: 0, userSelect: "none" }}>
            {ctx === "studio" ? "\u25B8" : "\u2022"}
          </span>
          <span style={bulletTextStyle}>
            {renderInline(line.slice(2))}
          </span>
        </div>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(line)) {
      const num = line.match(/^(\d+)\./)?.[1] ?? "";
      nodes.push(
        <div key={i} style={{ display: "flex", gap: 6, margin: ctx === "studio" ? "3px 0" : "2px 0", paddingLeft: 8 }}>
          <span style={{ color: ctx === "studio" ? VOJOMO_PURPLE : "#94a3b8", flexShrink: 0, fontSize: base, minWidth: 16, fontFamily: "inherit" }}>{num}.</span>
          <span style={bulletTextStyle}>
            {renderInline(line.replace(/^\d+\.\s/, ""))}
          </span>
        </div>
      );
      continue;
    }

    // Horizontal rule
    if (trimmed === "---" || trimmed === "***" || trimmed === "___") {
      nodes.push(<hr key={i} style={{ border: "none", borderTop: `1px solid ${ctx === "studio" ? "#e5e7eb" : "#e2e8f0"}`, margin: hrMargin }} />);
      continue;
    }

    // Blank line
    if (trimmed === "") {
      nodes.push(<div key={i} style={{ height: blankHeight }} />);
      continue;
    }

    // Paragraph
    nodes.push(
      <p key={i} style={pStyle}>
        {renderInline(line)}
      </p>
    );
  }

  return nodes;
}
